import React from 'react';
import { X, Clock, Users, Star, Gift, Calendar, Check } from 'lucide-react';
import { Button } from './ui/button';

const PackageDetailsModal = ({ isOpen, onClose, package: pkg, onBookPackage }) => {
  if (!isOpen || !pkg) return null;

  const handleBookPackage = () => {
    onClose();
    onBookPackage(pkg);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-lg sm:rounded-2xl shadow-2xl w-full max-w-xs sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden animate-zoom-in">
        {/* Header */}
        <div className="relative">
          <img 
            src={pkg.image} 
            alt={pkg.name}
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all duration-300"
          >
            <X size={20} />
          </button>
          
          {pkg.popular && (
            <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Popular Choice
            </div>
          )}
          
          <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-20">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2">{pkg.name}</h2>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-white/90 text-sm sm:text-base">
              <span className="flex items-center">
                <Clock size={16} className="mr-1" />
                {pkg.duration}
              </span>
              <span className="flex items-center">
                <Users size={16} className="mr-1" />
                Up to {pkg.maxGuests} guests
              </span>
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className={`${
                      i < Math.floor(pkg.rating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-white/50'
                    }`}
                  />
                ))}
                <span className="ml-1 text-sm">({pkg.reviews} reviews)</span>
              </div>
            </div>
          </div>
          
          <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg">
            <div className="text-lg sm:text-xl font-bold">৳{pkg.price.toLocaleString()}</div>
            <div className="text-xs opacity-90">{pkg.duration}</div>
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8 max-h-[55vh] sm:max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Package Details */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-4">Package Description</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {pkg.description}
                </p>
                <div className="flex items-center space-x-6 text-sm text-gray-500">
                  <span>Duration: {pkg.duration}</span>
                  <span>Max Guests: {pkg.maxGuests}</span>
                  <span>Category: {pkg.category}</span>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4">Package Highlights</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pkg.highlights && pkg.highlights.length > 0 ? pkg.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                      <span className="text-gray-800 font-medium">{highlight}</span>
                    </div>
                  )) : (
                    <p className="text-gray-500">Package highlights will be available soon.</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4">What's Included</h3>
                <div className="grid grid-cols-2 gap-4">
                  {pkg.inclusions && pkg.inclusions.length > 0 ? pkg.inclusions.map((inclusion, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <inclusion.icon className="text-blue-600 flex-shrink-0" size={20} />
                      <span className="font-medium">{inclusion.text}</span>
                    </div>
                  )) : (
                    <p className="text-gray-500 col-span-2">Inclusion details will be available soon.</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4">Sample Itinerary</h3>
                <div className="space-y-4">
                  {/* Sample itinerary based on package type */}
                  {pkg.category === 'romantic' && (
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                        <div>
                          <h4 className="font-semibold">Day 1 - Arrival & Welcome</h4>
                          <p className="text-gray-600 text-sm">Check-in, welcome drinks, romantic room setup, candlelit dinner</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                        <div>
                          <h4 className="font-semibold">Day 2 - Spa & Relaxation</h4>
                          <p className="text-gray-600 text-sm">Couples spa treatment, leisure time, special dinner arrangements</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {pkg.category === 'business' && (
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                        <div>
                          <h4 className="font-semibold">Business Setup</h4>
                          <p className="text-gray-600 text-sm">Executive room, meeting room access, business center facilities</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                        <div>
                          <h4 className="font-semibold">Professional Services</h4>
                          <p className="text-gray-600 text-sm">Airport transfers, express services, business breakfast</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {pkg.category === 'family' && (
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                        <div>
                          <h4 className="font-semibold">Family Activities</h4>
                          <p className="text-gray-600 text-sm">Kids club activities, family room setup, welcome treats</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                        <div>
                          <h4 className="font-semibold">Entertainment & Dining</h4>
                          <p className="text-gray-600 text-sm">Pool access, family dining, activity vouchers</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {pkg.category === 'wellness' && (
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                        <div>
                          <h4 className="font-semibold">Wellness Journey</h4>
                          <p className="text-gray-600 text-sm">Spa treatments, yoga sessions, healthy dining options</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                        <div>
                          <h4 className="font-semibold">Fitness & Relaxation</h4>
                          <p className="text-gray-600 text-sm">Fitness center access, meditation, wellness amenities</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Booking Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-xl p-6 sticky top-0">
                <h3 className="text-xl font-semibold mb-4">Book This Package</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Package Price</span>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        ৳{pkg.price.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500">{pkg.duration}</div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Original Price</span>
                    <span className="text-gray-500 line-through">
                      ৳{pkg.originalPrice.toLocaleString()}
                    </span>
                  </div>

                  <div className="border-t pt-4">
                    <div className="text-green-600 font-semibold">
                      Save ৳{(pkg.originalPrice - pkg.price).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={handleBookPackage}
                    className="w-full py-3"
                  >
                    Book This Package
                  </Button>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      Flexible booking • Free cancellation available
                    </p>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="mt-6 pt-6 border-t space-y-3 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Star className="mr-2 text-yellow-400" size={16} />
                    {pkg.rating}/5 from {pkg.reviews} reviews
                  </div>
                  <div>
                    <strong>Best for:</strong> {
                      pkg.category === 'romantic' ? 'Couples, Anniversaries, Honeymoons' :
                      pkg.category === 'business' ? 'Business travelers, Corporate events' :
                      pkg.category === 'family' ? 'Families with children' :
                      pkg.category === 'wellness' ? 'Wellness enthusiasts, Spa lovers' :
                      'All travelers'
                    }
                  </div>
                  <div>
                    <strong>Booking period:</strong> Available year-round with advance booking
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageDetailsModal;